import type {
  CreateProjectMutation,
  CreateProjectMutationVariables,
  GenerateAccessTokensMutation,
  GenerateAccessTokensMutationVariables,
  GetProjectsQuery,
  GetProjectsQueryVariables,
  RefreshAccessTokensMutation,
  RefreshAccessTokensMutationVariables,
  SignPayloadMutation,
  SignPayloadMutationVariables
} from '@/graphql/API';
import { JwtTokens, ProjectListItem, SignedPayload } from '@/graphql/API';
import {
  generateAccessTokens,
  refreshAccessTokens,
  signPayload
} from '@/graphql/mutations';
import { createProjectMutation } from '@/graphql/project/mutations';
import { getProjectQuery, getProjectsQuery } from '@/graphql/project/queries';
import { executeMutation, executeQuery } from '@/utils/helpers/graphql';

export const createProjectRequest = async (): Promise<string | undefined> => {
  const result = await executeMutation<
    CreateProjectMutationVariables,
    CreateProjectMutation
  >({
    query: createProjectMutation,
    variables: {
      input: {
        title: 'turbo-project',
        baseListingUrl: '/elanlar',
        chatEnabled: true
      }
    }
  });

  if (!result || !result.createProject) {
    throw new Error('Failed to create project');
  }

  return result.createProject.id;
};

export const getProjectsRequest = async (): Promise<
  Array<ProjectListItem> | undefined
> => {
  const result = await executeQuery<
    GetProjectsQueryVariables,
    GetProjectsQuery
  >({
    query: getProjectsQuery
  });

  const items = result?.getProjects?.items?.filter(
    (item): item is ProjectListItem => item !== null
  );

  return items;
};

interface Project {
  id: string;
  title: string;
  signatureSecretKey: string;
  jwtSecretKey: string;
  jwtAccessTokenExpiresIn: string;
  jwtRefreshTokenExpiresIn: string;
  baseListingUrl: string;
  chatEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export const getProjectRequest = async (id): Promise<Project | undefined> => {
  const result = await executeQuery<
    GetProjectsQueryVariables,
    GetProjectsQuery
  >({
    query: getProjectQuery,
    variables: { id }
  });

  const items = result?.getProject;

  return items;
};

export const fetchSignPayload = async (
  projectId: string,
  userId: string,
  name: string,
  phones?: string,
  photo?: string
): Promise<SignedPayload | undefined> => {
  const result = await executeMutation<
    SignPayloadMutationVariables,
    SignPayloadMutation
  >({
    query: signPayload,
    variables: {
      input: {
        projectId,
        userId,
        name,
        phones,
        photo
      }
    }
  });

  if (!result?.signPayload) {
    throw new Error('Failed to get sign payload');
  }

  return result.signPayload;
};

export const fetchAccessTokens = async (
  timestamp: number,
  signature: string,
  projectId: string,
  userId: string,
  name: string,
  phones?: string,
  photo?: string
): Promise<JwtTokens | undefined> => {
  const result = await executeMutation<
    GenerateAccessTokensMutationVariables,
    GenerateAccessTokensMutation
  >({
    query: generateAccessTokens,
    variables: {
      input: {
        payload: {
          projectId,
          userId,
          name,
          phones,
          photo,
          timestamp
        },
        signature
      }
    }
  });

  if (!result?.generateAccessTokens) {
    throw new Error('Failed to generate access tokens');
  }

  return result.generateAccessTokens;
};

export const refreshAccessTokensRequest = async (
  refreshToken: string
): Promise<JwtTokens | undefined> => {
  const result = await executeMutation<
    RefreshAccessTokensMutationVariables,
    RefreshAccessTokensMutation
  >({
    query: refreshAccessTokens,
    variables: {
      input: {
        refreshToken
      }
    }
  });

  if (!result?.refreshAccessTokens) {
    throw new Error('Failed to refresh access tokens');
  }

  return result.refreshAccessTokens;
};
